import { useEffect } from 'react';

import type { ClinicalNote } from '@/react-query/videa-notes/useExistingNotes';
import { useUpdateNotes } from '@/react-query/videa-notes/useUpdateNotes';

import { createNoteUpdatePayload, detectNoteChanges } from '../helpers';
import type { EditedFields } from '../types';

interface UseNoteAutoSaveProps {
  selectedNote: ClinicalNote | undefined;
  noteContent: string;
  noteWithTemplateData: ClinicalNote | undefined;
  editedFields: EditedFields;
  hasWritebackEnabled: boolean;
  isResettingRef: React.MutableRefObject<boolean>;
  prevContentRef: React.MutableRefObject<string>;
  prevTemplateRef: React.MutableRefObject<string | null>;
  setIsSavingNoteUpdate: (saving: boolean) => void;
  setEditedFields: (fields: EditedFields) => void;
  refetchFullNote: () => Promise<unknown>;
}

export const useNoteAutoSave = ({
  selectedNote,
  noteContent,
  noteWithTemplateData,
  editedFields,
  hasWritebackEnabled,
  isResettingRef,
  prevContentRef,
  prevTemplateRef,
  setIsSavingNoteUpdate,
  setEditedFields,
  refetchFullNote,
}: UseNoteAutoSaveProps) => {
  const { mutate: updateNote } = useUpdateNotes();

  useEffect(() => {
    // Skip auto-save if note is currently being reset to avoid race conditions
    if (!selectedNote?.id || isResettingRef.current) return;

    const changeDetection = detectNoteChanges(
      noteContent,
      noteWithTemplateData,
      editedFields,
      hasWritebackEnabled,
      prevContentRef,
      prevTemplateRef,
    );

    if (!changeDetection || !changeDetection.hasChanges) return;

    const { noteWithoutBlurb, isTemplateNote, fieldsChanged } = changeDetection;

    // Capture both content and fields when changes are detected
    // This ensures consistent behavior: both are captured at the same time,
    // and if user makes more changes during debounce, effect re-runs and cancels this timeout
    const contentBeingSaved = noteWithoutBlurb;
    const fieldsBeingSaved = editedFields;
    const fieldsChangedForPayload = fieldsChanged;

    const timeoutId = setTimeout(() => {
      setIsSavingNoteUpdate(true);

      const updatePayload = createNoteUpdatePayload(
        contentBeingSaved,
        isTemplateNote,
        fieldsChangedForPayload,
        noteWithTemplateData,
        fieldsBeingSaved,
      );

      updateNote(
        {
          clinicalNoteId: selectedNote.id,
          updateNotesBody: updatePayload,
        },
        {
          onSuccess: () => {
            setIsSavingNoteUpdate(false);
            // Update refs to track the newly saved content, preventing duplicate saves
            if (isTemplateNote) {
              prevTemplateRef.current = contentBeingSaved;
            } else {
              prevContentRef.current = contentBeingSaved;
            }
            // Clear fields consistently with how we track content (via refs)
            // Any new changes made during save will trigger a new auto-save cycle
            setEditedFields(null);
            // Refetch after clearing fields to avoid useNoteSync overwriting user changes
            // Since refs are already updated, useNoteSync will see content matches and won't overwrite
            refetchFullNote();
          },
          onError: () => {
            setIsSavingNoteUpdate(false);
          },
        },
      );
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    noteContent,
    selectedNote?.id,
    noteWithTemplateData,
    hasWritebackEnabled,
    editedFields,
    isResettingRef,
    prevContentRef,
    prevTemplateRef,
    setIsSavingNoteUpdate,
    setEditedFields,
    refetchFullNote,
    updateNote,
  ]);
};
